import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Flex } from 'rebass/styled-components';

import { UiStateContext } from '../../contexts/ui-state/UiStateContext';
import { addLocationDistance } from '../../distance';
import useGrid from '../../hooks/grid/useGrid';
import useKendraGetByIdApi from '../../hooks/kendra-api/useKendraGetByIdApi';
import useRandomize from '../../hooks/randomize/useRandomize';
import { DeviceType } from '../../hooks/use-breakpoint/device-type';
import useBreakpoint from '../../hooks/use-breakpoint/useBreakpoint';
import useFeatureToggle from '../../hooks/use-feature-toggle/useFeatureToggle';
import { FeatureToggle, IconPack, Location, LocationCardGridContent } from '../../models/app-data-model';
import { Location as KendraLocation } from '../../services/kendra-api/kendra-api-service';
import CardGridContainerDesktop from '../card-grid-container-desktop/card-grid-container-desktop';
import ErrorBanner from '../error-banner/error-banner';
import LoadingSpinner from '../loading-spinner/loading-spinner';
import LocationCardGridCardCompact from '../location-card-grid-card-compact/location-card-grid-card-compact';
import LocationCardGridCard from '../location-card-grid-card/location-card-grid-card';
import LocationCardGridMobile from '../location-card-grid-mobile/location-card-grid-mobile';

export type LocationCardGridProps = {
  content: LocationCardGridContent;
  featureToggles: FeatureToggle[];
  iconPack: IconPack[];
};

export type CardRow = {
  cards: Location[];
};

export const mapKendraLocation = (kendra: KendraLocation): Location => {
  return {
    location_id: kendra.location_id,
    title: kendra.location_name,
    address: {
      street_address: kendra.street_address,
      city: kendra.city || '',
      state: kendra.state || '',
      zip_code: kendra.zip_code
    },
    website: {
      title: '',
      href: kendra._source_uri
    },
    photo: {
      title: '',
      href: kendra.location_pic
    },
    hours: kendra.location_hours !== 'null' ? kendra.location_hours : '',
    status: 'active',
    distance: kendra.distance,
    feedback_token: kendra.feedback_token
  } as Location;
};

const LocationCardGrid: React.FC<LocationCardGridProps> = ({ content, featureToggles, iconPack }: LocationCardGridProps) => {
  const showLocationCardGrid: boolean = useFeatureToggle('location-card-grid', featureToggles);
  const uiStateContext = useContext(UiStateContext);
  const randomCards = useRandomize(content.cards);

  const csLocations = (content.randomize_card_order ? randomCards : content.cards)
    .filter((card) => card.location_card.location_reference?.length > 0 && card.location_card.location_reference[0].status === 'active')
    .map((card) => addLocationDistance(card.location_card.location_reference[0], uiStateContext));

  const [locationIds] = useState(
    content.cards.filter((card) => card.location_card.location_reference?.length === 0).map((card) => card.location_card.location_id)
  );

  const deviceType = useBreakpoint();
  const [kendraApi, setLocationIds] = useKendraGetByIdApi(
    [],
    'locations',
    showLocationCardGrid,
    content.randomize_card_order,
    mapKendraLocation
  );

  const locations = [...(kendraApi?.responses || []), ...csLocations];
  const cardGrid = useGrid(locations, content?.max_columns);

  const refreshData = useCallback(() => {
    setLocationIds(locationIds);
  }, [locationIds, setLocationIds]);

  useEffect(() => {
    refreshData();
  }, [content.cards, setLocationIds, locationIds, refreshData]);

  if (!showLocationCardGrid) {
    return null;
  }

  const multiColumnRenderings = cardGrid?.map((cardRow, rowIndex) => {
    const cardRowClass = `card-row ${rowIndex === 0 ? 'first-row' : ''} ${rowIndex + 1 === cardGrid.length ? 'last-row' : ''}`;

    return content.max_columns === 3 ? (
      <div data-testid={`cardrow${rowIndex}`} key={rowIndex} className={cardRowClass}>
        {cardRow.items?.map((card, index) => (
          <LocationCardGridCard iconPack={iconPack} key={index} cardWidth={content.max_columns} location={card as Location} />
        ))}
      </div>
    ) : (
      <div data-testid={`cardrow${rowIndex}`} key={rowIndex} className={cardRowClass}>
        {cardRow.items?.map((card, index) => (
          <LocationCardGridCardCompact iconPack={iconPack} key={index} location={card as Location} />
        ))}
      </div>
    );
  });

  const singleColumnRenderings = locations.map((card, index) => (
    <LocationCardGridCard iconPack={iconPack} key={index} cardWidth={1} location={card as Location} />
  ));

  const desktopColumnRenderings = (
    <CardGridContainerDesktop maxColumns={content?.max_columns} tertiaryButton={content?.tertiary_button}>
      {content?.max_columns > 1 && deviceType > DeviceType.Tablet ? multiColumnRenderings : singleColumnRenderings}
    </CardGridContainerDesktop>
  );

  const cardGridRenderings =
    deviceType <= DeviceType.Tablet ? <LocationCardGridMobile content={content} apiResponse={locations} /> : desktopColumnRenderings;

  return (
    <>
      {kendraApi.isRunning ? (
        <LoadingSpinner />
      ) : kendraApi.hasError ? (
        <Flex justifyContent="center">
          <ErrorBanner onReload={refreshData} />
        </Flex>
      ) : (
        cardGridRenderings
      )}
    </>
  );
};

export default LocationCardGrid;
